import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { TrendingUp, TrendingDown, Minus, Zap, Battery, ArrowUp, ArrowDown, Flame, Droplets, RefreshCw } from "lucide-react";
import { MirrorCard } from "@/components/MirrorCard";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";

const MOOD_LABELS: Record<string, string> = {
  calm: "Calm",
  anxious: "Anxious",
  energized: "Energized",
  creative: "Creative",
  drained: "Drained",
  overwhelmed: "Overwhelmed",
};

interface TrendDay {
  date: string;
  mood: string;
  moodScore: number;
  energyAvg: number | null;
  entries: number;
}

interface TrendsData {
  period: string;
  empty?: boolean;
  message?: string;
  totalEntries: number;
  daysActive: number;
  avgMoodScore: number;
  avgEnergy: number | null;
  moodTrend: TrendDay[];
  moodDistribution: Record<string, number>;
  topDrains: Array<{ label: string; count: number }>;
  topFuels: Array<{ label: string; count: number }>;
  mentalLoad: Array<{ theme: string; count: number; trend: string }>;
  topTags: Array<{ tag: string; count: number }>;
}

function MoodScoreGauge({ score }: { score: number }) {
  const color = "var(--accent-color)";
  const label = score >= 65 ? "Steady" : score >= 45 ? "Mixed" : "Rough";
  const [animated, setAnimated] = useState(false);
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!animated) return;
    let frame: number;
    const duration = 800;
    const start = performance.now();
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * score));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [animated, score]);

  const dashLen = (score / 100) * 213.6;

  return (
    <div className="flex flex-col items-center gap-2 relative w-20 h-20" data-testid="mood-gauge">
      <div>
        <svg viewBox="0 0 80 80" className="w-full h-full">
          <circle cx="40" cy="40" r="34" fill="none" style={{ stroke: "var(--border-hairline)" }} strokeWidth="5" />
          <circle
            cx="40" cy="40" r="34"
            fill="none" strokeWidth="5"
            strokeDasharray={animated ? `${dashLen} 213.6` : "0 213.6"}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
            style={{ stroke: color, transition: "stroke-dasharray 1s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-lg font-semibold">
          <span style={{ color, fontFamily: "var(--font-display)", fontWeight: 600 }}>
            {displayScore}
          </span>
        </div>
      </div>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--type-label-size)",
          letterSpacing: "var(--type-label-tracking)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function MiniTrendLine({ data, height = 48 }: { data: TrendDay[]; height?: number }) {
  if (data.length < 2) return null;

  const scores = data.map(d => d.moodScore);
  const min = Math.min(...scores) - 0.05;
  const max = Math.max(...scores) + 0.05;
  const range = max - min || 1;
  const w = 100;

  const points = scores.map((s, i) => ({
    x: (i / (scores.length - 1)) * w,
    y: height - ((s - min) / range) * (height - 8) - 4,
  }));

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");

  const lastScore = scores[scores.length - 1];
  const color = "var(--accent-color)";

  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }}>
      <defs>
        <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.15 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d={`${pathD} L ${w} ${height} L 0 ${height} Z`} fill="url(#trendGrad)" />
      <path d={pathD} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: color }} />
      {points.map((p, i) => (
         <circle key={i} cx={p.x} cy={p.y} r="2.5" style={{ fill: "var(--accent-color)" }} />
      ))}
    </svg>
  );
}

function MoodBar({ mood, count, total }: { mood: string; count: number; total: number }) {
  const pct = Math.round((count / total) * 100);
  const color = "var(--accent-color)";

  return (
    <div className="flex items-center gap-3" data-testid={`mood-bar-${mood}`}>
      <span
        className="w-20 text-right"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--type-label-size)",
          letterSpacing: "var(--type-label-tracking)",
          textTransform: "uppercase",
        }}
      >
        {MOOD_LABELS[mood] || mood}
      </span>
      <div className="flex-1 h-2 rounded-full" style={{ background: "var(--surface-3)" }}>
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: color, transition: "width 0.6s ease", minWidth: pct > 0 ? 4 : 0 }}
        />
      </div>
      <span
        className="w-8 font-semibold"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "0.75rem",
          fontWeight: 500,
        }}
      >
        {pct}%
      </span>
    </div>
  );
}

function TrendIcon({ trend }: { trend: string }) {
  if (trend === "up") return <ArrowUp size={11} style={{ color: "var(--accent-text-soft)" }} />;
  if (trend === "down") return <ArrowDown size={11} style={{ color: "var(--accent-color)" }} />;
  if (trend === "new") return (
    <span
      style={{
        color: "var(--accent-text-soft)",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--type-label-size)",
        letterSpacing: "var(--type-label-tracking)",
        textTransform: "uppercase",
        fontWeight: 600,
      }}
    >
      NEW
    </span>
  );
  return <Minus size={11} />;
}

export default function Trends() {
  const [period, setPeriod] = useState<"week" | "month">("week");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<TrendsData>({
    queryKey: ["/api/trends", period],
    queryFn: async () => {
      const res = await fetch(`/api/trends?period=${period}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load trends");
      return res.json();
    },
  });

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["/api/trends", period] });
  }, [queryClient, period]);

  const { containerRef, isRefreshing, pullProgress, pullIndicatorStyle, handlers } = usePullToRefresh({
    onRefresh: handleRefresh,
  });

  return (
    <AtmosphericPage contained atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }} contentClassName="flex flex-col h-full">
      <div
        className="flex items-center justify-between px-6 py-4 border-b"
        style={{ borderColor: "var(--border-medium)" }}
      >
        <div>
          <h1
            className="text-lg"
            style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}
            data-testid="text-trends-title"
          >
            Trends
          </h1>
          <p
            className="mt-0.5"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              letterSpacing: "var(--type-label-tracking)",
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            how you've been
          </p>
        </div>

        <div
          className="flex rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--border-medium)" }}
        >
          {(["week", "month"] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className="touch-target px-3 py-1.5 transition-all"
              style={{
                fontFamily: "var(--font-display)",
                color: period === p ? "var(--fg-raw)" : "var(--text-secondary)",
                background: period === p ? "var(--accent-bg-strong)" : "transparent",
                fontWeight: period === p ? 600 : 400,
              }}
              data-testid={`button-period-${p}`}
            >
              {p === "week" ? "7 DAYS" : "30 DAYS"}
            </button>
          ))}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto"
        ref={containerRef}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onTouchEnd={handlers.onTouchEnd}
      >
        <div style={pullIndicatorStyle} data-testid="pull-indicator-trends">
          {isRefreshing ? (
            <div className="pull-spinner" />
          ) : pullProgress > 0 ? (
            <RefreshCw
              size={16}
              style={{
                color: "var(--accent-color)",
                opacity: pullProgress,
                transform: `rotate(${pullProgress * 360}deg)`,
                transition: "transform 0.1s ease",
              }}
            />
          ) : null}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--type-label-size)",
                letterSpacing: "var(--type-label-tracking)",
                textTransform: "uppercase",
              }}
            >
              LOADING...
            </div>
          </div>
        ) : data?.empty ? (
          <div className="flex flex-col items-center justify-center h-64 gap-5 px-8 text-center fade-in" data-testid="empty-trends">
            <div
              className="w-16 h-16 rounded-full breathe"
              style={{ border: "1px solid var(--accent-border)", background: "var(--accent-bg)" }}
            />
            <div className="text-xl" style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}>
              Still gathering data
            </div>
            <div
              className="text-sm"
              style={{ color: "var(--text-secondary)", fontFamily: "var(--font-reflective)" }}
            >
              Keep talking to Fractal. Trends appear after a few conversations.
            </div>
          </div>
        ) : data ? (
          <div className="px-5 py-5 flex flex-col" style={{ gap: "20px" }}>
            <div data-testid="section-overview">
              <MoodScoreGauge score={data.avgMoodScore} />
              <div className="flex-1 pt-1 flex items-baseline gap-2 mb-3">
                <div>
                  <span
                    className="font-semibold"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "var(--type-label-size)",
                      letterSpacing: "var(--type-label-tracking)",
                      textTransform: "uppercase",
                    }}
                  >
                    <span style={{ fontFamily: "var(--font-display)" }}>{data.totalEntries}</span> ENTRIES ACROSS{" "}
                    <span style={{ fontFamily: "var(--font-display)" }}>{data.daysActive}</span> DAYS
                  </span>
                </div>
                <MiniTrendLine data={data.moodTrend ?? []} />
                <div className="mt-1.5 whitespace-nowrap" style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--type-label-size)",
                  letterSpacing: "var(--type-label-tracking)",
                  textTransform: "uppercase",
                }}>
                  {(data.moodTrend ?? []).length > 0
                    ? `${new Date(data.moodTrend[0].date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(data.moodTrend[data.moodTrend.length - 1].date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                    : ""}
                </div>
              </div>
            </div>

            <MirrorCard label="MOOD BREAKDOWN" testId="section-mood-dist">
              <div className="space-y-2">
                {Object.entries(data.moodDistribution ?? {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([mood, count]) => (
                    <MoodBar key={mood} mood={mood} count={count} total={data.totalEntries} />
                  ))
                }
              </div>
            </MirrorCard>

            {((data.topDrains ?? []).length > 0 || (data.topFuels ?? []).length > 0) && (
              <div data-testid="section-drains-fuels">
                {data.avgEnergy !== null && (
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={13} style={{ color: "var(--accent-text-soft)" }} />
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--type-label-size)",
                        letterSpacing: "var(--type-label-tracking)",
                        textTransform: "uppercase",
                        color: "var(--text-muted)",
                      }}
                    >
                      Avg energy
                    </span>
                    <span
                      className="font-semibold"
                      style={{
                        color: "var(--accent-text-soft)",
                        fontFamily: "var(--font-display)",
                      fontSize: "0.9375rem",
                      }}
                    >
                      {data.avgEnergy}/10
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                <div
                  className="rounded-xl p-4"
                  style={{ background: "var(--accent-bg)", border: "1px solid var(--accent-border)" }}
                >
                  <div className="flex items-center gap-1.5 mb-3">
                    <Droplets size={12} style={{ color: "var(--accent-text-soft)" }} />
                    <span
                      style={{
                        color: "var(--accent-text-soft)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--type-label-size)",
                        letterSpacing: "var(--type-label-tracking)",
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      DRAINS
                    </span>
                  </div>
                  {(data.topDrains ?? []).length > 0 ? (
                    <div className="space-y-1.5">
                      {(data.topDrains ?? []).map((d, i) => (
                        <div key={i} className="flex items-center justify-between truncate">
                           <span style={{ fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                            {d.label}
                          </span>
                          <span
                            className="ml-2 font-semibold"
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "var(--type-label-size)",
                            }}
                          >
                            {d.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                     <span style={{ fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                      Nothing yet
                    </span>
                  )}
                </div>

                <div
                  className="rounded-xl p-4"
                  style={{ background: "var(--accent-bg)", border: "1px solid var(--accent-border)" }}
                >
                  <div className="flex items-center gap-1.5 mb-3">
                    <Flame size={12} style={{ color: "var(--accent-color)" }} />
                    <span
                      style={{
                        color: "var(--accent-color)",
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--type-label-size)",
                        letterSpacing: "var(--type-label-tracking)",
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      FUEL
                    </span>
                  </div>
                  {(data.topFuels ?? []).length > 0 ? (
                    <div className="space-y-1.5">
                      {(data.topFuels ?? []).map((f, i) => (
                        <div key={i} className="flex items-center justify-between truncate">
                           <span style={{ fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                            {f.label}
                          </span>
                          <span
                            className="ml-2 font-semibold"
                            style={{
                              fontFamily: "var(--font-display)",
                              fontSize: "var(--type-label-size)",
                            }}
                          >
                            {f.count}x
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                     <span style={{ fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                      Nothing yet
                    </span>
                  )}
                </div>
                </div>
              </div>
            )}

            {(data.mentalLoad ?? []).length > 0 && (
              <MirrorCard label="WHAT'S ON YOUR MIND" testId="section-mental-load">
                <div className="space-y-2">
                  {(data.mentalLoad ?? []).map((item, i) => {
                    const maxCount = (data.mentalLoad ?? [])[0]?.count ?? 1;
                    const pct = Math.round((item.count / maxCount) * 100);
                    const color = "var(--accent-color)";

                    return (
                      <div key={item.theme} className="flex items-center gap-3 flex-1" data-testid={`mental-load-${i}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                             <span style={{ fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                              {item.theme}
                            </span>
                            <TrendIcon trend={item.trend} />
                          </div>
                          <div className="h-1.5 rounded-full" style={{ background: "var(--surface-3)" }}>
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${pct}%`, background: color, opacity: 0.7, transition: "width 0.6s ease" }}
                            />
                          </div>
                        </div>
                        <span
                          className="font-semibold"
                          style={{
                            fontFamily: "var(--font-display)",
                            fontSize: "var(--type-label-size)",
                          }}
                        >
                          {item.count}x
                        </span>
                      </div>
                    );
                  })}
                </div>
              </MirrorCard>
            )}

            <div className="pb-4" />
          </div>
        ) : null}
      </div>
    </AtmosphericPage>
  );
}
